/**
 * PdfViewerModal — iter134m
 *
 * Renders a PDF blob (or authenticated URL) INSIDE the app using
 * react-pdf + pdfjs-dist. This bypasses every native-plugin path
 * that failed in iter134j–l:
 *
 *  - No dependency on ``@capacitor/browser`` (needs cap sync)
 *  - No dependency on ``@capacitor/filesystem`` + ``@capacitor/share``
 *  - No dependency on the WebView having a native PDF renderer
 *    (Capacitor's stripped-down WebView on Android doesn't)
 *  - Works identically on Android, iOS, and the browser
 *
 * Fetches the PDF via authenticated axios, feeds the ArrayBuffer to
 * pdf.js, renders each page to a ``<canvas>``. User can pinch-zoom,
 * scroll pages, hit the "Save" button which delegates to whichever
 * OS mechanism actually works on this device (Filesystem+Share if
 * available; download anchor on web).
 */
import { Component, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { X, Download, Loader2, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";
import { api } from "./api";
import { filenameFromContentDisposition } from "./pdfDownload";

const _CAP = () => (typeof window !== "undefined" ? window.Capacitor : null);

// iter134m — bundle the pdf.js worker as a same-origin file so
// Capacitor WebView's ``file://`` scheme can load it (a CDN worker
// URL fails on native because of CSP + offline). We reuse the
// worker shipped by ``pdfjs-dist``.
// iter134o — bug reported by user: on the user's production server
// (``marketplace.localappstore.in``) the auto-emitted webpack asset
// path resolves to ``/static/media/pdf.wo...mjs`` — nginx serves
// ``.mjs`` files with the wrong MIME (``application/octet-stream``
// instead of ``application/javascript``) → dynamic import fails
// with "Setting up fake worker failed: Failed to fetch dynamically
// imported module".
//
// Fix: ship the worker at ``public/pdf.worker.min.js`` — a plain
// ``.js`` extension nginx maps to ``application/javascript`` by
// default, and CRA copies ``public/*`` verbatim to ``build/`` so
// the path is stable. The file is a byte-for-byte copy of
// ``node_modules/pdfjs-dist/build/pdf.worker.min.mjs`` — update via
// ``yarn sync-pdf-worker`` (see ``package.json``) after any
// ``pdfjs-dist`` bump.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

// iter134m/2 — Safety net around <Document>. If pdf.js throws
// synchronously (e.g. "Cannot perform Construct on a detached
// ArrayBuffer" during a re-render race) React would otherwise unmount
// the whole subtree with no user feedback. This boundary surfaces the
// error via ``onError`` so the parent can render a fallback + toast.
class PdfErrorBoundary extends Component {
    constructor(props) { super(props); this.state = { crashed: false }; }
    static getDerivedStateFromError() { return { crashed: true }; }
    componentDidCatch(error) {
        try { toast.error(error?.message || "PDF renderer crashed"); } catch { /* toast may not be ready */ }
        if (typeof this.props.onError === "function") {
            try { this.props.onError(error); } catch { /* swallow — surfaced via toast already */ }
        }
    }
    render() {
        if (this.state.crashed) return null;   // parent's ``err`` state renders the fallback UI
        return this.props.children;
    }
}

/**
 * Props:
 *   endpoint  — backend path, e.g. ``/admin/commission-invoices/{id}/pdf``
 *   filename  — download hint
 *   onClose   — parent handler
 */
export default function PdfViewerModal({ endpoint, filename, onClose }) {
    const [bytes, setBytes] = useState(null);   // Uint8Array — safe to pass repeatedly
    const [err, setErr] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [page, setPage] = useState(1);
    const [scale, setScale] = useState(1.0);
    const [savedFilename, setSavedFilename] = useState(filename || "invoice.pdf");
    const abortRef = useRef(null);

    // iter134m/2 — memoize the ``file`` prop so react-pdf sees the
    // SAME object reference between renders (parent re-renders would
    // otherwise trigger a pdf.js reload). We also wrap the underlying
    // ArrayBuffer in a Uint8Array copy — pdf.js uses ``postMessage``
    // with the ``[buffer]`` transfer list on first load which
    // DETACHES the raw ArrayBuffer in the main thread. Any subsequent
    // access then throws ``TypeError: Cannot perform Construct on a
    // detached ArrayBuffer``. A Uint8Array view keeps the caller's
    // buffer intact and lets pdf.js clone it internally.
    const fileProp = useMemo(() => (bytes ? { data: bytes } : null), [bytes]);

    useEffect(() => {
        const controller = new AbortController();
        abortRef.current = controller;
        (async () => {
            try {
                const { data, headers } = await api.get(endpoint, {
                    responseType: "arraybuffer",
                    signal: controller.signal,
                });
                const ctype = (headers["content-type"] || "").toLowerCase();
                if (!ctype.includes("application/pdf")) {
                    setErr("Server did not return a PDF");
                    return;
                }
                const fn = filenameFromContentDisposition(
                    headers["content-disposition"], filename || "invoice.pdf",
                );
                setSavedFilename(fn);
                // COPY into a fresh Uint8Array so pdf.js's transfer
                // list can't detach the original ``data`` ArrayBuffer.
                setBytes(new Uint8Array(data));
            } catch (e) {
                if (e?.code !== "ERR_CANCELED") {
                    setErr(e?.response?.data?.detail || e?.message || "Failed to load PDF");
                }
            }
        })();
        return () => controller.abort();
    }, [endpoint, filename]);

    // iter134s — track Save flight so double-taps don't fire two
    // network calls / two Share sheets.
    const [saving, setSaving] = useState(false);
    const saveOrShare = async () => {
        if (saving) return;
        setSaving(true);
        try { await _doSaveOrShare(); }
        finally { setSaving(false); }
    };

    const _doSaveOrShare = async () => {
        // WhatsApp says "The file you picked was not a document" and
        // a PDF viewer says "Cannot display PDF". Root cause: the
        // pdf.js Document consumed our ``bytes`` Uint8Array during
        // rendering (pdf.js's worker uses ``postMessage`` with a
        // transferable buffer on some code paths → the backing
        // ArrayBuffer on the main thread ends up detached / zeroed).
        // By the time Save runs, ``new Blob([bytes])`` yields either
        // an empty blob or garbage bytes → the saved file is not a
        // valid PDF.
        //
        // Fix: re-fetch the PDF fresh from the server for Save. The
        // network cost is trivial (~3 KB) and guarantees the bytes
        // are pristine — no matter what pdf.js did to our in-memory
        // copy. Also: use axios ``responseType: 'blob'`` so we can
        // pass the Blob directly to Filesystem/Share without a
        // base64 encode step (which was another failure surface).
        const cap = _CAP();
        const isNative = cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform();

        let freshBlob;
        try {
            const { data, headers } = await api.get(endpoint, { responseType: "blob" });
            const ctype = (headers["content-type"] || "").toLowerCase();
            if (!ctype.includes("application/pdf")) {
                toast.error("Server didn't return a PDF for saving.");
                return;
            }
            freshBlob = data instanceof Blob ? data : new Blob([data], { type: "application/pdf" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Couldn't fetch the PDF");
            return;
        }

        if (isNative) {
            try {
                const [{ Filesystem, Directory }, { Share }] = await Promise.all([
                    import("@capacitor/filesystem"),
                    import("@capacitor/share"),
                ]);
                const base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onerror = () => reject(reader.error);
                    reader.onload = () => {
                        const d = String(reader.result || "");
                        const idx = d.indexOf(",");
                        resolve(idx >= 0 ? d.slice(idx + 1) : d);
                    };
                    reader.readAsDataURL(freshBlob);
                });
                if (!base64 || base64.length < 100) {
                    // Sanity — the encoder ran but produced empty
                    // data. Bail rather than write a 0-byte "PDF".
                    toast.error("PDF content unavailable — please try again.");
                    return;
                }
                const cleanName = savedFilename.replace(/[\\/:*?"<>|]+/g, "_");
                const written = await Filesystem.writeFile({
                    path: cleanName, data: base64,
                    directory: Directory.Cache, recursive: true,
                });
                await Share.share({
                    title: cleanName, text: cleanName, url: written.uri,
                    dialogTitle: "Save or share invoice",
                });
                toast.success("Choose where to save the PDF");
                return;
            } catch (e) {
                const s = String(e && e.message || e);
                if (/cancel|abort|dismiss/i.test(s) || (e && e.name === "AbortError")) return;
                const isPluginMissing = /unimplemented|not implemented|not registered|no such module|not available|api not available/i.test(s);
                toast.error(
                    isPluginMissing
                        ? "PDF sharing needs an app update. Please reinstall the latest APK."
                        : `Couldn't save the PDF: ${s || "unknown error"}`,
                    { duration: 8000 },
                );
                return;
            }
        }

        // Web / desktop — classic anchor download works reliably.
        try {
            const url = URL.createObjectURL(freshBlob);
            const a = document.createElement("a");
            a.href = url; a.download = savedFilename; a.rel = "noopener";
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (e) {
            toast.error(e?.message || "Could not save the PDF");
        }
    };

    // iter134p — auto-fit the page width to the modal body on mobile
    // so the "Amount" column doesn't get clipped at 100%. We measure
    // the container and pass ``width={containerWidth * scale}`` to
    // react-pdf's Page — it internally scales without needing us to
    // compute a scale ratio.
    const bodyRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    useEffect(() => {
        if (!bodyRef.current) return;
        const el = bodyRef.current;
        const measure = () => {
            // 32px accounts for the p-3 (12px) padding on both sides
            // + a hair of breathing room so the shadow doesn't clip.
            const w = Math.max(0, (el.clientWidth || 0) - 32);
            setContainerWidth(w);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [bytes]);

    return (
        <div
            data-testid="pdf-viewer-modal"
            className="fixed inset-0 z-[100] bg-black/80 flex flex-col"
            onClick={onClose}
            style={{
                // iter134u — iOS notch / Dynamic Island cut off the
                // toolbar with the Close + Save buttons because the
                // modal uses ``fixed inset-0`` which bypasses body-
                // level safe-area padding. Push the whole modal below
                // the status bar and above the home indicator so all
                // controls (including Save/Share) are tappable.
                paddingTop: "env(safe-area-inset-top, 0px)",
                paddingBottom: "env(safe-area-inset-bottom, 0px)",
                paddingLeft: "env(safe-area-inset-left, 0px)",
                paddingRight: "env(safe-area-inset-right, 0px)",
            }}
        >
            {/* Toolbar */}
            <div
                className="bg-neutral-900 text-white px-3 py-2 flex items-center gap-2 border-b border-neutral-800 shrink-0"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    data-testid="pdf-close"
                    onClick={onClose}
                    className="p-2 rounded hover:bg-white/10 active:scale-95 transition-transform shrink-0"
                    aria-label="Close"
                ><X size={18} /></button>
                <div className="text-xs font-semibold truncate flex-1 min-w-0">{savedFilename}</div>
                {/* iter134u — right cluster ``flex-shrink-0`` so Save
                    is never pushed off-screen on narrow iPhones. The
                    zoom-% indicator is hidden below 360px to keep
                    the row on one line. */}
                <div className="flex items-center gap-1 shrink-0">
                    <button
                        onClick={() => setScale((s) => Math.max(0.5, +(s - 0.25).toFixed(2)))}
                        className="p-2 rounded hover:bg-white/10 active:scale-95 transition-transform"
                        aria-label="Zoom out"
                    ><ZoomOut size={16} /></button>
                    <span className="hidden xs:inline text-[11px] tabular-nums w-10 text-center">
                        {Math.round(scale * 100)}%
                    </span>
                    <button
                        onClick={() => setScale((s) => Math.min(3, +(s + 0.25).toFixed(2)))}
                        className="p-2 rounded hover:bg-white/10 active:scale-95 transition-transform"
                        aria-label="Zoom in"
                    ><ZoomIn size={16} /></button>
                    <button
                        data-testid="pdf-save"
                        onClick={saveOrShare}
                        disabled={!bytes || saving}
                        className="ml-1 inline-flex items-center gap-1 rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-xs font-bold active:scale-95 transition-transform disabled:opacity-50 shrink-0"
                    >
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>

            {/* Body */}
            <div
                ref={bodyRef}
                className="flex-1 overflow-auto p-3 flex flex-col items-center bg-neutral-800"
                onClick={(e) => e.stopPropagation()}
            >
                {err ? (
                    <div className="text-red-300 text-sm text-center py-16 max-w-sm">
                        <p className="font-semibold">Couldn&apos;t load the PDF</p>
                        <p className="text-xs mt-1 opacity-80">{err}</p>
                    </div>
                ) : !bytes ? (
                    <div className="text-white/70 text-sm py-16"><Loader2 size={20} className="animate-spin mx-auto mb-2" /> Loading invoice…</div>
                ) : (
                    <PdfErrorBoundary
                        onError={(e) => setErr(e?.message || "Failed to render PDF")}
                    >
                        <Document
                            file={fileProp}
                            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                            onLoadError={(e) => setErr(e?.message || "Failed to render PDF")}
                            loading={<div className="text-white/70 py-10"><Loader2 size={20} className="animate-spin" /></div>}
                            className="flex flex-col items-center gap-3"
                        >
                            {/* iter134p — pass ``width`` so the Amount
                                column isn't clipped on narrow phones.
                                ``scale`` multiplies width so pinch-zoom
                                still works from the toolbar. */}
                            <Page
                                pageNumber={page}
                                width={containerWidth ? containerWidth * scale : undefined}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                className="shadow-2xl bg-white"
                            />
                        </Document>
                    </PdfErrorBoundary>
                )}
            </div>

            {/* Pager */}
            {numPages > 1 && (
                <div
                    className="bg-neutral-900 text-white px-3 py-2 flex items-center justify-center gap-3 border-t border-neutral-800 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        className="p-2 rounded hover:bg-white/10 active:scale-95 transition-transform disabled:opacity-40"
                    ><ChevronLeft size={16} /></button>
                    <span className="text-xs tabular-nums">Page {page} of {numPages}</span>
                    <button
                        onClick={() => setPage((p) => Math.min(numPages, p + 1))}
                        disabled={page >= numPages}
                        className="p-2 rounded hover:bg-white/10 active:scale-95 transition-transform disabled:opacity-40"
                    ><ChevronRight size={16} /></button>
                </div>
            )}
        </div>
    );
}
